import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useAsyncEffect from 'use-async-effect';
import { FiArrowLeft, FiMail, FiUsers, FiFileText, FiLoader, FiCheck } from 'react-icons/fi';
import { IGlobalState } from '../../../model/IGlobalState';
import { NewCampaignInfo } from '../../../model/GetCampaignsResult';
import api from '../../../api/api';
import { toast } from '../Toast';
import { pluralize, to, fillPreviewLine } from '../../../util/util';

// default (unnamed) list and the reserved Shareholders list (Constants.ShareholdersListName, synced daily from the register)
const labels: Record<string, string> = { '': 'Investors — everyone who signed up', Shareholders: 'Shareholders on the register with an email' };
const steps = [
    'Save this and Investi keeps it as a draft — nothing is sent.',
    'The email builder opens so you can write it.',
    'Send yourself a test, then send it.',
    'Once it goes out, a Statistics tab appears here.',
];

// spec 2:59251. No campaign name (the subject is the label), no autosave, no template usage stats.
const NewCampaign: React.FC = () => {
    const history = useHistory();
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const [info, setInfo] = useState<NewCampaignInfo | null>(null);
    const [subject, setSubject] = useState('');
    const [previewLine, setPreviewLine] = useState('');
    const [audience, setAudience] = useState<string[]>([]);
    const [templateId, setTemplateId] = useState('');
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    useAsyncEffect(async isMounted => {
        if (!stock) return;
        const [err, data] = await to(api().getNewCampaignInfo(stock.id));
        if (!isMounted()) return;
        if (err || !data) { setError(err?.message ?? 'Loading failed'); return; }
        const i: NewCampaignInfo = data;
        setInfo(i);
        const defaultList = i.lists.find(x => !x.name);
        setAudience(defaultList ? [defaultList.id] : []);
        setTemplateId(i.templates[0]?.id ?? '');
    }, [stock?.id]);

    const toggle = (id: string) => setAudience(a => a.includes(id) ? a.filter(x => x !== id) : [...a, id]);
    const back = () => history.push('/communications/email-campaigns');

    const save = async () => {
        if (subject.trim().length < 5) { toast.error('The subject line must be at least 5 characters'); return; }
        if (audience.length === 0) { toast.error('Choose who receives it'); return; }
        if (!templateId) { toast.error('Choose a template'); return; }
        setSaving(true);
        const result = await api().saveCampaign({
            stockId: stock.id, campaignId: '', templateId, subject: subject.trim(), editorContent: '', html: fillPreviewLine(info?.templates.find(t => t.id === templateId)?.html ?? '', previewLine),
            listId: audience, previewLine: previewLine.trim(),
        }).catch(e => ({ errorMessage: e.message, campaignId: '' }));
        setSaving(false);
        if (result.errorMessage) { toast.error(`Save failed: ${result.errorMessage}`); return; }
        history.push(`/communications/campaign/${encodeURIComponent(result.campaignId)}`);
    };

    const rows = (info?.lists ?? []).map(l => ({ id: l.id, label: labels[l.name ?? ''] ?? l.name, count: l.numSubscribers }));

    return <div className="new-campaign">
        <button type="button" className="link back" onClick={back}><FiArrowLeft /> Back to Email campaigns</button>
        <div className="page-header">
            <div>
                <h1>New Campaign</h1>
                <p>Start with the basics. Nothing is sent yet — once you save, this becomes a draft you can come back to.</p>
            </div>
            <button type="button" className="btn-outline" onClick={back} disabled={saving}>Cancel</button>
            <button type="button" className="btn-primary" onClick={save} disabled={saving || !!error}>{saving && <FiLoader className="spin" />} Save and Continue</button>
        </div>
        {error && <p className="error">{error}</p>}

        <div className="columns">
            <div className="main">
                <div className="card">
                    <div className="card-head"><span className="icon-tile"><FiMail /></span><div><h2>Campaign Details</h2><p className="sub">This is what investors see</p></div></div>
                    <label className="field">
                        <span className="label">Subject line <em>what they see in the inbox</em></span>
                        <input value={subject} onChange={e => setSubject(e.target.value)} />
                    </label>
                    <label className="field">
                        <span className="label">Preview line <em>(optional)</em></span>
                        <input value={previewLine} onChange={e => setPreviewLine(e.target.value)} />
                    </label>
                    <div className="two">
                        <label className="field"><span className="label">From name</span><input value={info?.fromName ?? ''} disabled /></label>
                        <label className="field"><span className="label">Reply to</span><input value={info?.replyTo ?? ''} disabled /></label>
                    </div>
                </div>

                <div className="card">
                    <div className="card-head"><span className="icon-tile"><FiUsers /></span><div><h2>Who Receives It</h2><p className="sub">Only contacts who agreed to this kind of email are counted</p></div></div>
                    {rows.map(r => <label key={r.id} className={`audience${audience.includes(r.id) ? ' checked' : ''}`}>
                        <input type="checkbox" checked={audience.includes(r.id)} onChange={() => toggle(r.id)} />
                        <span className="box">{audience.includes(r.id) && <FiCheck />}</span>
                        <strong>{r.label}</strong>
                        <span className="count">{pluralize('contact', r.count)}</span>
                    </label>)}
                </div>

                <div className="card">
                    <div className="card-head"><span className="icon-tile"><FiFileText /></span><div><h2>Start from a Template</h2><p className="sub">You can change the words and pictures in the next step</p></div></div>
                    <div className="templates">
                        {(info?.templates ?? []).map(t => <label key={t.id!} className={`template${t.id === templateId ? ' selected' : ''}`}>
                            <div className="thumb"><iframe title={t.name} sandbox="" srcDoc={t.html.replace('{{content_placeholder}}', '')} tabIndex={-1} /></div>
                            <div className="t">
                                <input type="radio" name="template" checked={t.id === templateId} onChange={() => setTemplateId(t.id!)} />
                                <strong>{t.name}</strong>
                                {t.id === templateId && <span className="pill ok">Selected</span>}
                            </div>
                        </label>)}
                    </div>
                </div>
            </div>

            <div className="side">
                <div className="card next">
                    <h3>What happens next</h3>
                    <ol>{steps.map((s, i) => <li key={i}><span className="n">{i + 1}</span>{s}</li>)}</ol>
                </div>
            </div>
        </div>
    </div>;
};

export default NewCampaign;
